import { serverTimestamp, Timestamp } from "firebase/firestore";
import { t } from "i18next";
import { action, computed, makeObservable } from "mobx";
import { TKey } from "../../../shared/translations/translationKeys";
import type { UserReaction } from "../../activities/ActivityModel";
import { REACTIONS, type Reaction } from "../../activities/types";
import type { Api } from "../../core/apis";
import type { BaseModelData } from "../../core/BaseModel";
import { BaseModel } from "../../core/BaseModel";
import type { Stores } from "../../core/stores";
import { assertNever } from "../../core/utils";
import { Permission } from "../../permissions/types";
import type { IconProp } from "../../ui/icons";
import {
  type BusinessFeedCommentData,
  BusinessFeedCommentModel,
} from "../feedcomment/BusinessFeedCommentModel";
import type { BusinessStaticUserInfoWithUserUid } from "../plan/BusinessPlanModel";
import type { FeedItemShoutOutData } from "./events/FeedItemShoutout";
import type { FeedItemTextData } from "./events/FeedItemText";
import { getFeedIntroText } from "./events/FeedItemUserIntro";
import { buildFeedMentionsFromText } from "./utils";

const MAX_COMMENTS_IN_CACHE = 2;

export interface BaseFeedModelData extends BaseModelData {
  pinned: boolean;
  hidden: boolean;
  deleted: boolean;
  edited: boolean;
  reported: number;
}

export enum BusinessFeedEventType {
  GroupShoutOut = "b_plan_shoutout",
  Text = "b_plan_text",
  UserCompletedIntro = "b_user_intro",
  BusinessModuleStatus = "b_module_status",
}

export enum BusinessFeedMentionType {
  User = "user",
  Group = "group",
  Schedule = "schedule",
  Task = "task",
  Info = "info",
}

export type BusinessFeedMention = {
  type: BusinessFeedMentionType;
  uid: string; // userUid or groupUid
  name: string; // name at the time of posting (substitution)
};

export interface BusinessFeedItemData extends BaseFeedModelData {
  businessPlanUid: string;
  moduleUid: string;

  type: BusinessFeedEventType;
  itemData: unknown; // Snapshot set per type...

  comments: number;
  latestComments: BusinessFeedCommentData[]; // cache of latest comments

  reactions: UserReaction[];
  mentions: BusinessFeedMention[];
  mentionUids: string[]; // for efficient query
}

export class BusinessFeedItem extends BaseModel<BusinessFeedItemData> {
  constructor(stores: Stores, api: Api, data: BusinessFeedItemData) {
    super(stores, api, data);

    Object.assign(this.data, {
      reactions: this.data.reactions ?? [],
      mentions: this.data.mentions ?? [],
      mentionUids: this.data.mentionUids ?? [],

      pinned: this.data.pinned ?? false,
      hidden: this.data.hidden ?? false,
      deleted: this.data.deleted ?? false,
      edited: this.data.edited ?? false,
      reported: this.data.reported ?? 0,
    });

    this.loadCache();

    makeObservable(this);
  }

  loadCache() {
    // Add cached items as models
    const feedComments = this.data.latestComments.map(
      (d) => new BusinessFeedCommentModel(this.stores, this.api, d),
    );
    this.stores.businessFeedCommentsStore.load(feedComments);
  }

  @computed get isPinned(): boolean {
    return this.businessPlan.data.feedPinnedEvents.some(
      (d) => d.uid === this.uid,
    );
  }

  @action async updateTimestampToNow() {
    this.data.ts = serverTimestamp() as Timestamp;
    await this.api.businessFeedItemsApi.update(this.uid, { ts: this.data.ts });

    // If in cache already, delete it first
    this.businessPlan.deleteFromCacheWithoutSave(this);

    // Add it to the top
    this.businessPlan.addToCacheWithoutSave(this);

    // Save it
    await this.businessPlan.updateFeedItemInCache(this);
  }

  @action async toggleReaction(reaction: Reaction) {
    const businessPlan = this.businessPlan;
    const currentUserUid = businessPlan?.userInPlan?.data.uid;

    if (!currentUserUid || !businessPlan) {
      return undefined;
    }

    const hasReacted = this.data.reactions.findIndex(
      (r) => r.reaction === reaction && r.userUid === currentUserUid,
    );

    if (hasReacted !== -1) {
      this.data.reactions.splice(hasReacted, 1);
    } else {
      this.data.reactions.push({
        userUid: currentUserUid,
        reaction,
      });
    }

    await this.api.businessFeedItemsApi.update(this.data.uid, {
      reactions: this.data.reactions,
    });

    void businessPlan.updateFeedItemInCache(this);
  }

  @action async edit(text: string) {
    // @ts-expect-error
    this.data.itemData.text = text;

    const { mentions, mentionUids } = buildFeedMentionsFromText(
      text,
      this.businessPlan,
    );
    this.data.mentions = mentions;
    this.data.mentionUids = mentionUids;

    await this.api.businessFeedItemsApi.set(this);

    return this.saveEditAction("edited");
  }

  @action async saveEditAction(
    option: "edited" | "deleted" | "hidden" | "reported" | "pinned",
  ) {
    const businessPlan = this.businessPlan;

    if (!businessPlan) {
      return undefined;
    }

    if (option === "edited") this.data.edited = true;
    if (option === "deleted") this.data.deleted = true;
    if (option === "hidden") this.data.hidden = !this.data.hidden;
    if (option === "pinned") this.data.pinned = !this.data.pinned;
    if (option === "reported") this.data.reported++;

    await this.api.businessFeedItemsApi.update(this.data.uid, {
      edited: this.data.edited,
      deleted: this.data.deleted,
      hidden: this.data.hidden,
      pinned: this.data.pinned,
      reported: this.data.reported,
    });

    void businessPlan.updateFeedItemInCache(this);
  }

  @action updateFeedCommentInCache(item: BusinessFeedCommentModel) {
    const inCacheIndex = this.data.latestComments.findIndex(
      (f) => f.uid === item.uid,
    );

    if (inCacheIndex !== -1) {
      // Update in place
      this.data.latestComments[inCacheIndex] = {
        ...item.toFireStore(),
        uid: item.uid,
      };
      void this.api.businessFeedItemsApi.update(this.uid, {
        latestComments: this.data.latestComments,
      });
    }

    this.businessPlan?.updateFeedItemInCache(this);
  }

  @computed get isAuthor(): boolean {
    const author = this.maybeAuthor;
    const user = this.businessPlan?.userInPlan;

    return !!user && !!author && author.userUid === user.uid;
  }

  @computed get isAdmin(): boolean {
    return this.businessPlan.checkPermission(Permission.CanManageFeedItems);
  }

  @computed get hasReactions(): boolean {
    return this.data.reactions.length > 0;
  }

  @computed get reactionRowItemCounts(): {
    icon: IconProp | string;
    count: number;
  }[] {
    const anyReaction: { icon: IconProp | string; count: number }[] = [];
    REACTIONS.forEach((r) => {
      const c = this.data.reactions.filter(
        (rr) => rr.reaction === r.type,
      ).length;
      if (c) {
        anyReaction.push({
          icon: r.icon,
          count: c,
        });
      }
    });
    return anyReaction;
  }

  @computed get businessPlan() {
    const b = this.stores.businessPlansStore.byId.get(
      this.data.businessPlanUid,
    );

    if (!b) {
      throw Error("No business plan found");
    }

    return b;
  }

  async addComment(
    item: Omit<
      BusinessFeedCommentData,
      | "uid"
      | "feedItemUid"
      | "ts"
      | "reactions"
      | "mentions"
      | "mentionUids"
      | "authorUserUid"
      | "latestReplies"
      | "repliesCount"
      | "businessPlanUid"
      | "edited"
      | "pinned"
      | "deleted"
      | "hidden"
      | "reported"
    >,
  ) {
    const businessPlan = this.businessPlan;
    if (!businessPlan) {
      throw Error("No plan found for feeditem");
    }

    if (!businessPlan.userInPlan) {
      throw Error("No user found for feeditem");
    }

    const feedComment: Omit<BusinessFeedCommentData, "uid"> = {
      ...item,
      authorUserUid: businessPlan.userInPlan.uid,
      businessPlanUid: businessPlan.uid,
      ts: serverTimestamp() as Timestamp,
      feedItemUid: this.data.uid,
      reactions: [],
      repliesCount: 0,
      hidden: false,
      deleted: false,
      edited: false,
      pinned: false,
      reported: 0,
      latestReplies: [],
      ...buildFeedMentionsFromText(item.text, this.businessPlan),
    };

    const feedCommentModel =
      await this.api.businessFeedCommentsApi.add(feedComment);

    // Save a cache in item
    this.data.latestComments.push({
      ...feedCommentModel.toFireStore(),
      uid: feedCommentModel.uid,
    });

    this.data.comments++;
    if (this.data.latestComments.length > MAX_COMMENTS_IN_CACHE) {
      this.data.latestComments.shift();
    }

    void this.api.businessFeedItemsApi.update(this.uid, {
      comments: this.data.comments,
      latestComments: this.data.latestComments,
    });

    void businessPlan.updateFeedItemInCache(this);

    const user = businessPlan.userInPlan;
    const parentAuthor = this.maybeAuthor;
    if (user && parentAuthor) {
      void this.businessPlan.addEmailData({
        item: {
          ts: feedCommentModel.data.ts ?? Timestamp.now(),
          uid: feedCommentModel.uid,
          author: {
            uid: user.uid,
            name: user.data.displayName,
          },
          type: "feed_comment",
          textSID: TKey.BusinessFeedCommentPosted,
          textData: { comment: feedCommentModel.data.text },

          parent: {
            uid: this.uid,
            ts: this.data.ts ?? Timestamp.now(),
            author: {
              uid: parentAuthor.userUid,
              name: parentAuthor.displayName,
            },
            type: "feed",
            textSID: TKey.BusinessFeedItemPosted,
            textData: { comment: this.textRepresentationInEmail },
          },
        },
      });
    }
  }

  @computed get textRepresentationInEmail(): string {
    switch (this.data.type) {
      case BusinessFeedEventType.Text:
        return (this.data.itemData as FeedItemTextData).text;
      case BusinessFeedEventType.GroupShoutOut:
        return (this.data.itemData as FeedItemShoutOutData).text;
      case BusinessFeedEventType.UserCompletedIntro:
        return getFeedIntroText();
      case BusinessFeedEventType.BusinessModuleStatus:
        return t("business:feed_task_status_updated");
      default:
        return assertNever(this.data.type);
    }
  }

  @computed get comments(): BusinessFeedCommentModel[] {
    const items: BusinessFeedCommentModel[] = [];

    const data = this.stores.businessFeedCommentsStore.all;
    for (const d of data) {
      if (d.data.feedItemUid === this.uid) {
        items.push(d);
      }
    }

    return items.sort((a, b) => ((a.data.ts ?? 0) > (b.data.ts ?? 0) ? 1 : -1));
  }

  @computed get hasMoreComments(): boolean {
    return this.comments.length < this.data.comments;
  }

  @computed get maybeAuthor(): BusinessStaticUserInfoWithUserUid | undefined {
    // @ts-expect-error
    const authorUid = this.data.itemData?.authorUid;
    if (authorUid) {
      // @ts-expect-error
      const u = this.businessPlan?.data.userInfo[this.data.itemData.authorUid];

      if (u) {
        return { ...u, userUid: authorUid };
      }
    }
    return undefined;
  }

  toFireStore(): Omit<BusinessFeedItemData, "uid"> {
    return {
      ...this.fromBaseModel(),
      ...this.data,
    };
  }
}
